// =============================================================================
// Shared autocomplete configs for the Territory / Sales Rep / Customer
// reference-data dropdowns.
//
// Single source of truth so every page (create-quote, quote list, customer
// search, …) populates and behaves identically. These mirror the create-quote
// page's design + data model: preloaded, JWT-scoped searchable dropdowns backed
// by refDataCache, with the same display/value/secondary fields.
//
// Used with the <SearchableSelect> component (variant="card").
// =============================================================================
import {
  territoriesDescriptor,
  salesRepsDescriptor,
  customersDescriptor,
  territoryCustomersDescriptor,
  allCustomersDescriptor,
  isAdminTerritory,
  isCustomerServiceRole,
  isCompanyWideCaller,
} from "./refDataCache";

export { isAdminTerritory, isCustomerServiceRole, isCompanyWideCaller };

// Territory — preloaded searchable dropdown. The full (JWT-scoped) territory
// list is cached once per session.
export const AUTOCOMPLETE_TERRITORY = {
  apiUrl: "/api/Territory/info",
  queryParam: "Search",
  dataPath: "territories",
  displayField: "territoryID",
  valueField: "territoryID",
  secondaryField: "territoryDesc",
  minChars: 1,
  preload: true,
  descriptor: territoriesDescriptor(),
};

// Sales Rep — reps in the (JWT-scoped) territory are bounded, so preload + cache
// the whole list. D99 is the admin/HQ territory and means "everywhere", so it
// never adds a territory filter (every rep is visible).
export function buildSalesRepConfig(territoryId) {
  const cfg = {
    apiUrl: "/api/SalesRep",
    queryParam: "search",
    dataPath: "salesReps",
    displayField: "salesRepCode",
    valueField: "salesRepCode",
    secondaryField: "name",
    minChars: 1,
  };
  if (territoryId && !isAdminTerritory(territoryId)) {
    cfg.defaultParams = `territoryId=${encodeURIComponent(territoryId)}`;
  }
  cfg.preload = true;
  cfg.descriptor = salesRepsDescriptor(territoryId);
  return cfg;
}

// Customer — scoped by the selected rep (or territory). Every branch is now a
// preloaded, session-cached list:
//   • rep picked        → that rep's customers (bounded)
//   • territory picked  → that territory's customers (resolved server-side
//                         via SalesTRPs, answered from the load-all cache)
//   • no narrowing      → the whole JWT-scoped base: full base for D99
//                         admins/distributors, the user's territory subtree
//                         for everyone else — one warm load-all slice either
//                         way, filtered client-side (instant on ~5.7k rows).
// This retired the per-keystroke Epicor live-search entirely, which took
// 1.4-9s per batch on the pilot tenant depending on load. The live-search
// config fields (queryTemplate & co.) are kept as the fallback the component
// uses if a preload can't load.
export function buildCustomerConfig(salesRepCode, territoryId, userScope) {
  const scope = userScope || {};
  let template = "(startswith(CustID,'{value}') or contains(Name,'{value}'))";
  // SAL-GAP-62: a Customer Service login is never narrowed to its own territory.
  if (!salesRepCode && !territoryId && !scope.isUnrestricted && scope.territoryId
      && !isCustomerServiceRole(scope.role)) {
    const t = String(scope.territoryId).replace(/'/g, "''");
    template += ` and (SalesRepCode eq '${t}' or startswith(SalesRepCode,'${t}-'))`;
  }
  const cfg = {
    apiUrl: "/api/Customer",
    queryParam: "filter",
    queryTemplate: template,
    dataPath: "customers",
    displayField: "custID",
    valueField: "custID",
    secondaryField: "name",
    minChars: 1,
  };
  const extras = [];
  if (salesRepCode) extras.push(`salesRepCode=${encodeURIComponent(salesRepCode)}`);
  else if (territoryId) extras.push(`territoryId=${encodeURIComponent(territoryId)}`);
  if (extras.length) cfg.defaultParams = extras.join("&");
  cfg.preload = true;
  if (salesRepCode) {
    cfg.descriptor = customersDescriptor(salesRepCode);
  } else if (territoryId && !isAdminTerritory(territoryId)) {
    cfg.descriptor = territoryCustomersDescriptor(territoryId);
  } else {
    cfg.descriptor = allCustomersDescriptor();
  }
  return cfg;
}

// -----------------------------------------------------------------------------
// withRefDataPreload — route a DB-configured autocomplete (DynamicSearch /
// DynamicForm field, whose config comes from FieldOptions JSON) through the
// session cache when it targets one of the cached reference datasets and its
// narrowing can be expressed as a cache key. Anything it can't map — extra
// params, clientFilter rules, unknown endpoints — is returned unchanged and
// keeps the live per-keystroke autocomplete.
// -----------------------------------------------------------------------------
export function withRefDataPreload(config) {
  if (!config || typeof config.apiUrl !== "string") return config;
  // clientFilter (e.g. the D99 parent-territory top-level-only rule) is only
  // applied by the live AutocompleteField — never preload past it.
  if (config.clientFilter) return config;

  const path = config.apiUrl.replace(/\/+$/, "").toLowerCase();
  let params;
  try {
    params = new URLSearchParams(config.defaultParams || "");
  } catch {
    return config;
  }
  const names = Array.from(params.keys()).map((k) => k.toLowerCase());
  const only = (...allowed) => names.every((n) => allowed.includes(n));
  const get = (name) => {
    for (const [k, v] of params.entries()) if (k.toLowerCase() === name) return v;
    return "";
  };

  let descriptor = null;
  if (path === "/api/territory/info") {
    if (names.length === 0) descriptor = territoriesDescriptor();
  } else if (path === "/api/salesrep") {
    // No params → the full (JWT-scoped) rep list; territoryId only → the
    // per-territory list. Both share keys with the filter-panel dropdowns.
    if (only("territoryid")) descriptor = salesRepsDescriptor(get("territoryid"));
  } else if (path === "/api/customer") {
    if (names.length === 0) descriptor = allCustomersDescriptor();
    else if (only("salesrepcode") && get("salesrepcode"))
      descriptor = customersDescriptor(get("salesrepcode"));
    else if (only("territoryid") && get("territoryid"))
      descriptor = territoryCustomersDescriptor(get("territoryid"));
  }

  return descriptor ? { ...config, preload: true, descriptor } : config;
}
